import EventEmitter from "events";
import io from "socket.io-client";
import eventsList from "./eventsList";
import { LIB_VERSION } from "./version";
export default class JawsClient extends EventEmitter {
    static DEFAULT_NAMESPACE = "dh.jasmin";
    static EVENT = eventsList;
    static TOKEN_EXPIRY_THRESHOLD = 15000;
    static RETRY_LIMIT = 10;
    stores;
    socket;
    retryCount = 0;
    isRequestingToken = false;
    isPageVisible = true;
    disconnectedByClient = false;
    config;
    eventSet;
    get namespace() {
        return this.config.namespace;
    }
    get autoConnect() {
        return this.config.autoConnect;
    }
    get socketUrl() {
        return this.config.socketUrl;
    }
    get authToken() {
        return this.config.authToken;
    }
    constructor(options) {
        super();
        this.config = {
            namespace: JawsClient.DEFAULT_NAMESPACE,
            tokenExpiryThreshold: JawsClient.TOKEN_EXPIRY_THRESHOLD,
            clientServerTimeDifference: 0,
            autoConnect: true,
            ...options,
        };
        this.setMaxListeners(50);
        this.stores = {};
        this.eventSet = new Set();
        this.socket = null;
        const requiredProps = [
            "socketUrl",
            "hostsName",
            ["authUrl", "authCallback"],
        ];
        requiredProps.forEach((param) => {
            if ((typeof param === "object" &&
                !Object.keys(this.config).some((item) => {
                    return param.includes(item);
                })) ||
                (typeof param === "string" && !this.config[param])) {
                throw new Error(`${param} must be defined.`);
            }
        });
        document?.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this));
        window?.addEventListener("online", this.connect.bind(this));
        window?.addEventListener("offline", this.disconnect.bind(this));
        this.autoConnect && this.connect();
    }
    /**
     * Creates store for the given event.
     * Store is simply a native Set()
     */
    createStore(name) {
        if (!this.stores[name]) {
            this.stores[name] = new Set();
        }
        return this.stores[name];
    }
    /**
     * Subscribe to the common events, which don't require authentication
     * @param {Array} eventNames Array containing event names to subscribe to.
     */
    subscribeEvents(eventNames) {
        this.socket?.connected && this.socket.emit(eventsList.ACTION.SUBSCRIBE, eventNames);
    }
    /**
     * Event handler when a connection error has occurred.
     * @param {Object}   error             Error object
     */
    onConnectError(error) {
        this.emit(eventsList.COMMON.CONNECT_ERROR, error);
        if (this.retryCount < JawsClient.RETRY_LIMIT) {
            this.retryCount += 1;
            setTimeout(() => {
                return this.connect();
            }, 1000);
        }
    }
    /**
     * Subscribes to all events coming from socket
     */
    subscribeToAllSocketEvents() {
        if (this.socket === null) {
            return;
        }
        // Enable wildcard events
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { onevent } = this.socket;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line func-names
        this.socket.onevent = function (packet) {
            const args = packet.data || [];
            onevent.call(this, packet);
            packet.data = ["*"].concat(args); // eslint-disable-line no-param-reassign
            onevent.call(this, packet);
        };
        this.socket.on("*", this.handleIncomingEvents.bind(this));
    }
    connectToServer() {
        return io(this.socketUrl, {
            auth: async (callback) => {
                callback(await this.getAuthToken()
                    .then((token) => {
                    return { token };
                })
                    .catch(() => {
                    return { interrupted: true };
                }));
            },
            forceNew: true,
            transports: ["websocket", "polling"],
            reconnectionAttempts: 10,
            query: {
                ver: `${this.config.hostsName}-${LIB_VERSION}`,
            },
        });
    }
    /**
     * Removes unnecessary stuff from the event name.
     * @param {string} eventName Long event name.
     * @returns {void|string|*}
     */
    parseEventName(eventName) {
        return eventName.replace(`${this.namespace}.`, "");
    }
    /**
     * Handles all incoming events from the socket server.
     * @param {string} name   Name of the incoming event
     * @param {object} header Socket request headers.
     * @param {string} body   Content of the event.
     */
    handleIncomingEvents(name, header, body) {
        if (!name) {
            throw Error();
        }
        const _name = this.parseEventName(name);
        switch (_name) {
            case eventsList.COMMON.AUTH_TIMEOUT:
                if (this.isPageVisible) {
                    this.reconnect();
                }
                else {
                    this.disconnectedByClient = true;
                    this.disconnect();
                }
                break;
            default:
                break;
        }
        if (header || body) {
            const store = this.stores[_name];
            if (store) {
                const value = { header, body };
                store.add(value);
                this.emit(_name, value, store, this);
            }
        }
    }
    async getAuthToken() {
        if (this.isTokenValid()) {
            const token = this.authToken;
            delete this.config.authToken;
            return token;
        }
        delete this.config.authToken;
        return this.config.authUrl ? this.fetchToken() : this.config.authCallback && this.config.authCallback();
    }
    /**
     * Propagate event to the JAWS server.
     * @param {string} name    event name of the client
     * @param {object} header  header of EDS event
     * @param {object} body    body of EDS event
     */
    propagateEvent(name, header, body) {
        this.socket && this.socket.emit(eventsList.ACTION.PROPAGATE, name, header, body);
    }
    /**
     * Overrides the inherited on method and subscribes all the non-common events.
     */
    on(type, listener) {
        if (!type) {
            console.warn("Event name must not be empty with listener:", listener);
            return this;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!Object.keys(eventsList.COMMON).includes(type) && !Object.values(eventsList.COMMON).includes(type)) {
            const namespacedType = `${this.namespace}.${type}`;
            if (!this.eventSet.has(namespacedType)) {
                this.eventSet.add(namespacedType);
                this.subscribeEvents([namespacedType]);
            }
            this.createStore(type);
        }
        super.on(type, listener);
        return this;
    }
    /**
     * Connects to the given socket URL
     */
    connect() {
        if (this.socket) {
            this.socket.connect();
            return;
        }
        try {
            this.socket = this.connectToServer();
            if (this.socket === null) {
                return;
            }
            this.subscribeToAllSocketEvents();
            this.socket.on("connect", () => {
                if (this.eventSet.size > 0) {
                    this.subscribeEvents(Array.from(this.eventSet));
                }
                this.retryCount = 0;
                this.emit(eventsList.COMMON.CONNECT);
            });
            this.socket.on("reconnect", () => {
                this.emit(eventsList.COMMON.RECONNECT);
                this.subscribeEvents(Array.from(this.eventSet));
            });
            this.socket.on("disconnect", () => {
                this.emit(eventsList.COMMON.DISCONNECT);
            });
            this.socket.on("connect_error", (e) => {
                return this.onConnectError(e);
            });
        }
        catch (e) {
            this.onConnectError(e);
            throw new Error(e);
        }
    }
    /**
     * Authenticates from session id
     */
    async fetchToken() {
        if (this.isRequestingToken) {
            console.warn("JAWS: Getting token already in progress.");
            return;
        }
        if (!this.config.authUrl) {
            console.warn("JAWS: No auth url provided.");
            return;
        }
        try {
            this.isRequestingToken = true;
            const response = await fetch(this.config.authUrl, { method: "GET" });
            const { token } = await response.json();
            if (!token) {
                throw new Error("No token");
            }
            return token;
        }
        catch (error) {
            throw new Error(`Could not fetch auth token: ${error.message}`);
        }
        finally {
            this.isRequestingToken = false;
        }
    }
    handleVisibilityChange() {
        this.isPageVisible = document.visibilityState === "visible";
        if (this.isPageVisible && this.disconnectedByClient) {
            this.disconnectedByClient = false;
            this.connect();
        }
    }
    /**
     * Authenticates into the JAWS server.
     */
    async authenticate() {
        const token = await this.getAuthToken();
        token &&
            this.socket &&
            this.socket.connected &&
            this.socket.emit(eventsList.ACTION.AUTH, { token }, (response) => {
                if (!response) {
                    throw new Error("JAWS: Authentication failed");
                }
            });
    }
    isTokenValid() {
        if (!this.authToken) {
            return false;
        }
        const [, payload] = this.authToken.split(".");
        const { exp } = JSON.parse(atob(payload));
        const expiryTime = exp * 1000 - this.config.tokenExpiryThreshold;
        const correctNow = Date.now() + this.config.clientServerTimeDifference;
        if (expiryTime < correctNow) {
            return false;
        }
        return true;
    }
    /**
     * Send stat log to the JAWS server.
     * @param {string} collectionName
     * @param {object} collectionData   Object of the statistical data
     */
    logStat(collectionName, collectionData) {
        this.socket && this.socket.emit(eventsList.ACTION.STAT, collectionName, collectionData);
    }
    /**
     * Forces client to disconnect from server
     */
    disconnect() {
        this.socket && this.socket.disconnect();
    }
    /**
     * Establish new ws socket and reassign existing listeners
     */
    reconnect() {
        this.disconnect();
        this.connect();
    }
    destroy() {
        Array.from(this.eventSet).forEach((eventName) => {
            this.removeAllListeners(eventName);
        });
        document?.removeEventListener("visibilitychange", this.handleVisibilityChange.bind(this));
        window?.removeEventListener("online", this.connect.bind(this));
        window?.removeEventListener("offline", this.disconnect.bind(this));
        this.disconnect();
        this.socket = null;
    }
}
